import { computed } from "vue";
import store from "@/store";
import {
  createAsyncState,
  fetchAsyncState,
  resetAsyncState
} from "@/composables/AsyncState";
import { buscarDadosPainel } from "../services/painelConsultor.service";
import type {
  Ata,
  Chamado,
  Consultor,
  PainelConsultorDados,
  Regional
} from "../types/painelConsultor.types";

/*
 * Estado no nível do módulo, não da instância: todas as telas e variações
 * (desktop e mobile) leem os mesmos dados, carregados uma vez só. Navegar entre
 * os níveis ou alternar desktop/mobile não dispara outra requisição.
 */
const estado = createAsyncState<PainelConsultorDados | null>(null, {
  shallow: true
});

/** Usuário dono dos dados: o escopo vem do token, então outro login recarrega. */
let carregadoPara: number | null = null;
let emAndamento: Promise<void> | null = null;

const agruparPorConsultor = <T extends { codConsultor: number }>(
  itens: T[]
): Map<number, T[]> => {
  const mapa = new Map<number, T[]>();
  itens.forEach((item) => {
    const lista = mapa.get(item.codConsultor) ?? [];
    lista.push(item);
    mapa.set(item.codConsultor, lista);
  });
  return mapa;
};

const indices = computed(() => {
  const dados = estado.data;

  return {
    regionais: new Map(
      (dados?.regionais ?? []).map((regional) => [
        regional.codRegional,
        regional
      ])
    ),
    consultores: new Map(
      (dados?.consultores ?? []).map((consultor) => [
        consultor.codConsultor,
        consultor
      ])
    ),
    chamados: agruparPorConsultor(dados?.chamados ?? []),
    atas: agruparPorConsultor(dados?.atas ?? [])
  };
});

const daLista =
  <T>(mapa: () => Map<number, T[]>) =>
  (codConsultores: number[]): T[] =>
    codConsultores.flatMap((cod) => mapa().get(cod) ?? []);

/**
 * Carrega uma vez por usuário. A carga em andamento é reaproveitada: o guard
 * das rotas e a página esperam a mesma requisição. Depois de falha, chamar de
 * novo tenta outra vez ("Tentar novamente").
 */
const carregar = (): Promise<void> => {
  const codUsuario = Number(store.getters.usuarioLogado?.codUsuario) || 0;

  if (codUsuario !== carregadoPara) {
    resetAsyncState(estado, null);
    carregadoPara = codUsuario;
    emAndamento = null;
  }

  if (estado.successfully) return Promise.resolve();

  emAndamento ??= fetchAsyncState(estado, buscarDadosPainel).finally(() => {
    emAndamento = null;
  });

  return emAndamento;
};

/**
 * Relê tudo depois de uma alteração (ata nova, status trocado) sem passar pelo
 * esqueleto: os dados atuais ficam na tela até a resposta chegar e continuam
 * lá se ela falhar.
 */
const atualizar = (): Promise<void> => {
  emAndamento = fetchAsyncState(estado, buscarDadosPainel).finally(() => {
    emAndamento = null;
  });

  return emAndamento;
};

const regional = (codRegional: number): Regional | null =>
  indices.value.regionais.get(codRegional) ?? null;

const consultor = (codConsultor: number): Consultor | null =>
  indices.value.consultores.get(codConsultor) ?? null;

const consultoresDa = (codRegional: number): Consultor[] =>
  (estado.data?.consultores ?? []).filter(
    (item) => item.codRegional === codRegional
  );

const chamadosDaLista = daLista<Chamado>(() => indices.value.chamados);
const atasDe = daLista<Ata>(() => indices.value.atas);

/** Nulo com o Jira indisponível (a rota de chamados falhou). */
const chamadosDe = (codConsultores: number[]): Chamado[] | null =>
  estado.data?.chamados === null ? null : chamadosDaLista(codConsultores);

export function useDadosPainelConsultor() {
  return {
    estado,
    carregar,
    atualizar,
    regional,
    consultor,
    consultoresDa,
    chamadosDe,
    atasDe
  };
}
